import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { ProfileCard } from '@components/assessors/atoms/ProfileCard/ProfileCard';
import { ProfileCardTitle } from '@components/assessors/atoms/ProfileCardTitle/ProfileCardTitle';
import { ProfileCardItem } from '@components/assessors/atoms/ProfileCardItem/ProfileCardItem';
import { ProfileCardSeparator } from '@components/assessors/atoms/ProfileCardSeparator/ProfileCardSeparator';
import type { User } from '@services/users';
import type { UserProfile } from '@services/profiles';
import { updateProfile } from '@services/profiles';
import { Alert } from '@acb_devel/design-system';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';

import styles from './ProfileData.module.scss';

export function ProfileData({
  user,
  setUser,
  loading,
  className,
}: {
  user: User | undefined;
  setUser: (user: User) => void;
  loading: boolean;
  className?: string;
}) {
  const { addToast } = useToastContext();

  const [profile, setProfile] = useState<UserProfile | null>(
    user?.profile ?? null
  );

  useEffect(() => {
    if (user?.profile) {
      setProfile(user.profile);
    }
  }, [user]);

  const handleUpdateField = async (
    field: keyof UserProfile,
    value: string | null
  ) => {
    try {
      if (!profile || !user) {
        return;
      }

      const updatedProfile = await updateProfile(profile.id, {
        ...profile,
        [field]: value,
      });

      setUser({
        ...user,
        profile: updatedProfile,
      });

      addToast(
        <ToastRoot>
          <Alert
            variant="success"
            direction="column"
            title={`Perfil actualizado`}
          />
        </ToastRoot>
      );
    } catch (error) {
      console.error(error);

      let errorMessage =
        'Asegúrate de que el campo esté en el formato correcto y que no se supere el límite de caracteres.';

      if (field === 'volunteerCode') {
        errorMessage =
          'Asegúrate de que el campo esté en el formato correcto y que el identificador no esté en uso por otro usuario.';
      }

      addToast(
        <ToastRoot>
          <Alert
            variant="error"
            direction="column"
            title={`Error al actualizar el perfil`}
            text={errorMessage}
          />
        </ToastRoot>
      );
    }
  };

  return (
    <div className={classNames(styles.profileData, className)}>
      <ProfileCard className={styles.profileData__dataCard}>
        <ProfileCardTitle>Datos básicos</ProfileCardTitle>
        <ProfileCardItem
          title="ID"
          value={user?.id.toString()}
          loading={loading}
        />
        <ProfileCardItem
          title="Código voluntario"
          value={
            user?.profile?.volunteerCode
              ? user?.profile?.volunteerCode.toString()
              : 'No identificado'
          }
          loading={loading}
          setValue={(newValue) =>
            handleUpdateField(
              'volunteerCode',
              newValue === '' ? null : newValue
            )
          }
        />
        <ProfileCardItem
          title="Nombre"
          value={user?.profile?.firstName}
          loading={loading}
          setValue={(newValue) => handleUpdateField('firstName', newValue)}
        />
        <ProfileCardItem
          title="Apellidos"
          value={user?.profile?.lastName}
          loading={loading}
          setValue={(newValue) => handleUpdateField('lastName', newValue)}
        />
        <ProfileCardItem
          title="Correo electrónico"
          value={user?.emailAddress}
          loading={loading}
        />
        {user?.profile?.dateOfBirth && (
          <ProfileCardItem
            title="Fecha de nacimiento"
            value={new Date(user.profile.dateOfBirth).toLocaleDateString(
              'es-ES',
              { year: 'numeric', month: 'numeric', day: 'numeric' }
            )}
            loading={loading}
          />
        )}
        <ProfileCardItem
          title="Ciudad"
          value={user?.profile?.city}
          loading={loading}
        />
        <ProfileCardItem
          title="Provincia"
          value={user?.profile?.province}
          loading={loading}
        />
        <ProfileCardSeparator />
        <ProfileCardItem
          title="DNI"
          value={user?.profile?.identificationNumber}
          loading={loading}
          setValue={(newValue) =>
            handleUpdateField('identificationNumber', newValue)
          }
        />
        <ProfileCardItem
          title="Teléfono"
          value={user?.profile?.phoneNumber}
          loading={loading}
          setValue={(newValue) => handleUpdateField('phoneNumber', newValue)}
        />
        <ProfileCardSeparator />
        <ProfileCardItem
          title="Skype"
          value={user?.profile?.skype}
          loading={loading}
          setValue={(newValue) => handleUpdateField('skype', newValue)}
        />
        <ProfileCardItem
          title="Instragram"
          value={user?.profile?.instagram}
          loading={loading}
          setValue={(newValue) => handleUpdateField('instagram', newValue)}
        />
        <ProfileCardItem
          title="X (Twitter)"
          value={user?.profile?.xTwitter}
          loading={loading}
          setValue={(newValue) => handleUpdateField('xTwitter', newValue)}
        />
        <ProfileCardItem
          title="TikTok"
          value={user?.profile?.tiktok}
          loading={loading}
          setValue={(newValue) => handleUpdateField('tiktok', newValue)}
        />
      </ProfileCard>
      <ProfileCard className={styles.profileData__dataCard}>
        <ProfileCardTitle>Experiencia</ProfileCardTitle>
        <ProfileCardItem
          title="Ocupación actual"
          value={user?.profile?.occupation}
          loading={loading}
        />
        <ProfileCardItem
          title="Formación"
          value={user?.profile?.education}
          loading={loading}
        />
        <ProfileCardItem
          title="Experiencia eventos deportivos"
          value={user?.profile?.previousExperience}
          loading={loading}
        />
        <ProfileCardItem
          title="Nivel de inglés"
          value={user?.profile?.englishLevel}
          loading={loading}
        />
        <ProfileCardSeparator />
        <ProfileCardItem
          title="Motivos para ser voluntario"
          value={user?.profile?.motivation}
          loading={loading}
        />
        <ProfileCardItem
          title="Relación con el baloncesto"
          value={user?.profile?.relationWithBasketball}
          loading={loading}
        />
        <ProfileCardItem
          title="Cómo nos ha conocido"
          value={user?.profile?.howDidYouFindUs}
          loading={loading}
        />
      </ProfileCard>
      <ProfileCard className={styles.profileData__dataCard}>
        <ProfileCardTitle>Preferencias</ProfileCardTitle>
        <ProfileCardItem
          title="Talla polo y sudadera"
          value={user?.profile?.shirtSize?.toUpperCase()}
          loading={loading}
          setValue={(newValue) =>
            handleUpdateField('shirtSize', newValue.toLowerCase())
          }
        />
        <ProfileCardItem
          title="Talla pantalón"
          value={user?.profile?.pantsSize?.toUpperCase()}
          loading={loading}
          setValue={(newValue) =>
            handleUpdateField('pantsSize', newValue.toLowerCase())
          }
        />
        <ProfileCardItem
          title="Acepta recibir información"
          value={user?.profile?.informAboutFutureEvents ? 'Sí' : 'No'}
          loading={loading}
        />
      </ProfileCard>
    </div>
  );
}
